// src/pages/maincourante/MainCouranteList.js - VERSION AVEC ICÔNES
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import jsPDF from "jspdf";
import "jspdf-autotable";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// Options de filtres
const MOTIF_OPTIONS = [
  { value: 'incident', label: 'Incident' },
  { value: 'observation', label: 'Observation' },
  { value: 'prise_service', label: 'Prise de service' },
  { value: 'fin_service', label: 'Fin de service' },
  { value: 'anomalie', label: 'Anomalie' }
];

// Fonction pour déterminer les couleurs selon le motif
const getMotifColor = (motif) => {
  const lowerMotif = (motif || '').toLowerCase();
  if (lowerMotif.includes('incident') || lowerMotif.includes('urgence')) {
    return { bg: '#ffebee', color: '#c62828' };
  } else if (lowerMotif.includes('observation')) {
    return { bg: '#e3f2fd', color: '#1565c0' };
  } else if (lowerMotif.includes('prise') || lowerMotif.includes('début') || lowerMotif.includes('debut')) {
    return { bg: '#e8f5e9', color: '#2e7d32' };
  } else if (lowerMotif.includes('fin') || lowerMotif.includes('clôture') || lowerMotif.includes('cloture')) {
    return { bg: '#fff3e0', color: '#ef6c00' };
  } else if (lowerMotif.includes('anomalie') || lowerMotif.includes('probleme')) {
    return { bg: '#fce4ec', color: '#c2185b' };
  } else {
    return { bg: '#f5f5f5', color: '#424242' };
  }
};

// En-tête commun : logo à gauche, nom/adresse/téléphone de la société connectée
// alignés sur la même ligne (à droite du logo, pas centrés en dessous), titre
// du rapport centré sous l'ensemble, puis séparateur avant le reste du
// contenu. Retourne la position Y à laquelle démarrer le contenu suivant.
const drawEnteteSociete = (doc, pageWidth, societeInfo, titre, defaultTitre) => {
  const logoSize = 24;
  const logoTop = 8;
  let logoBottom = logoTop;

  if (societeInfo?.logo) {
    const format = societeInfo.logo.startsWith('data:image/png') ? 'PNG'
      : societeInfo.logo.startsWith('data:image/webp') ? 'WEBP'
      : 'JPEG';
    try {
      doc.addImage(societeInfo.logo, format, 14, logoTop, logoSize, logoSize);
      logoBottom = logoTop + logoSize;
    } catch (e) {
      // Logo illisible (ex: SVG non supporté par jsPDF) : export sans logo plutôt que d'échouer.
    }
  }

  const infoX = 14 + logoSize + 6;
  let infoY = logoTop + 6;

  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(societeInfo?.nom || defaultTitre || "SecureControle", infoX, infoY);
  infoY += 5;

  if (societeInfo?.infoLigne) {
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(societeInfo.infoLigne, infoX, infoY);
    doc.setTextColor(0);
    infoY += 5;
  }

  let yPos = Math.max(logoBottom, infoY) + 6;

  doc.setFont(undefined, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text(titre, pageWidth / 2, yPos, { align: 'center' });
  doc.setFont(undefined, 'normal');
  yPos += 5;

  const sepY = yPos + 3;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(14, sepY, pageWidth - 14, sepY);
  yPos = sepY + 6;

  return yPos;
};

// Fonction pour générer un PDF récapitulatif de toutes les mains courantes
const generateMainCourantesReportPDF = (mainCourantes, societeInfo) => {
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();

    let yPos = drawEnteteSociete(doc, pageWidth, societeInfo, "RAPPORT DES MAINS COURANTES");

    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Statistiques
    const motifsCount = {};
    mainCourantes.forEach(mc => {
      const motif = mc.motif || 'Autre';
      motifsCount[motif] = (motifsCount[motif] || 0) + 1;
    });
    
    doc.setFontSize(14);
    doc.setTextColor(COLORS.primary);
    doc.text("STATISTIQUES", 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`Total des mains courantes : ${mainCourantes.length}`, 20, yPos);
    yPos += 7;
    
    // Tableau des motifs
    const tableData = Object.entries(motifsCount).map(([motif, count]) => [
      motif,
      count.toString(),
      `${((count / mainCourantes.length) * 100).toFixed(1)}%`
    ]);
    
    if (tableData.length > 0) {
      yPos += 5;
      doc.autoTable({
        head: [['Motif', 'Nombre', 'Pourcentage']],
        body: tableData,
        startY: yPos,
        theme: 'grid',
        headStyles: {
          fillColor: [11, 60, 93],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: { fontSize: 9 },
        margin: { left: 20, right: 20 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Liste détaillée
    doc.setFontSize(14);
    doc.setTextColor(COLORS.primary);
    doc.text("LISTE DÉTAILLÉE", 20, yPos);
    yPos += 10;
    
    // Tableau des mains courantes
    const mainCouranteData = mainCourantes.map(mc => [
      mc.id.toString(),
      mc.created_at_formatted,
      mc.site_nom || '-',
      mc.agent_nom || '-',
      mc.motif || '-'
    ]);
    
    if (mainCouranteData.length > 0) {
      doc.autoTable({
        head: [['ID', 'Date/Heure', 'Site', 'Agent', 'Motif']],
        body: mainCouranteData,
        startY: yPos,
        theme: 'grid',
        headStyles: {
          fillColor: [11, 60, 93],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 35 },
          2: { cellWidth: 40 },
          3: { cellWidth: 40 },
          4: { cellWidth: 40 }
        },
        margin: { left: 20, right: 20 },
        styles: { overflow: 'linebreak' },
        didDrawPage: function(data) {
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${data.pageNumber} sur ${data.pageCount}`,
            data.settings.margin.left,
            doc.internal.pageSize.height - 10
          );
        }
      });
    }
    
    // Sauvegarder le PDF
    const fileName = `rapport_mains_courantes_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la génération du rapport PDF:", error);
    
    // Version simplifiée en cas d'erreur — si elle réussit, un fichier est
    // bel et bien téléchargé : renvoyer false dans ce cas affichait à tort un
    // message d'erreur à l'utilisateur alors que le PDF venait d'être généré.
    try {
      const fallbackDoc = new jsPDF();
      fallbackDoc.setFontSize(16);
      fallbackDoc.text("Rapport des Mains Courantes", 20, 20);
      fallbackDoc.setFontSize(12);
      fallbackDoc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, 20, 30);
      fallbackDoc.text(`Total: ${mainCourantes.length} main(s) courante(s)`, 20, 40);
      fallbackDoc.text("Utilisez l'export CSV pour plus de détails.", 20, 50);
      fallbackDoc.save(`rapport_mains_courantes_simple_${new Date().toISOString().split('T')[0]}.pdf`);
      return true;
    } catch (fallbackError) {
      console.error("Erreur même avec la version simplifiée:", fallbackError);
    }

    return false;
  }
};

// Fonction pour générer un PDF détaillé pour une Note spécifique
const generateMainCourantePDF = (mainCourante, societeInfo) => {
  try {
    // Initialiser le PDF
    const doc = new jsPDF('p', 'mm', 'a4');

    // Dimensions de la page
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;

    let yPos = drawEnteteSociete(doc, pageWidth, societeInfo, "MAIN COURANTE - RAPPORT DÉTAILLÉ");

    // Informations principales
    doc.setFontSize(14);
    doc.setTextColor(COLORS.primary);
    doc.text("INFORMATIONS DE BASE", margin, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`ID : #${mainCourante.id}`, margin, yPos);
    yPos += 6;
    doc.text(`Date et heure : ${mainCourante.created_at_formatted}`, margin, yPos);
    yPos += 6;
    
    // Statut
    const statusColor = getMotifColor(mainCourante.motif);
    doc.setFillColor(parseInt(statusColor.bg.slice(1, 3), 16), parseInt(statusColor.bg.slice(3, 5), 16), parseInt(statusColor.bg.slice(5, 7), 16));
    doc.setTextColor(parseInt(statusColor.color.slice(1, 3), 16), parseInt(statusColor.color.slice(3, 5), 16), parseInt(statusColor.color.slice(5, 7), 16));
    doc.rect(margin, yPos - 2, 30, 6, 'F');
    doc.setTextColor(60);
    doc.text(`Motif : ${mainCourante.motif}`, margin + 35, yPos);
    yPos += 10;
    
    // Section Site
    doc.setFontSize(12);
    doc.setTextColor(COLORS.primary);
    doc.text("INFORMATIONS DU SITE", margin, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`Nom du site : ${mainCourante.site_nom || 'Non spécifié'}`, margin, yPos);
    yPos += 6;
    doc.text(`ID Site : ${mainCourante.site_id || 'Non spécifié'}`, margin, yPos);
    yPos += 10;
    
    // Section Agent
    doc.setFontSize(12);
    doc.setTextColor(COLORS.primary);
    doc.text("INFORMATIONS DE L'AGENT", margin, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`Nom : ${mainCourante.agent_nom || 'Non spécifié'}`, margin, yPos);
    yPos += 6;
    doc.text(`Matricule : ${mainCourante.agent_matricule || 'Non spécifié'}`, margin, yPos);
    yPos += 6;
    doc.text(`ID Agent : ${mainCourante.agent_id || 'Non spécifié'}`, margin, yPos);
    yPos += 10;
    
    // Section Description détaillée
    if (mainCourante.description) {
      doc.setFontSize(12);
      doc.setTextColor(COLORS.primary);
      doc.text("DESCRIPTION DÉTAILLÉE", margin, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setTextColor(60);
      
      // Gérer le texte long avec découpage
      const description = mainCourante.description || '';
      const maxWidth = pageWidth - 2 * margin;
      const lineHeight = 5;
      
      // Diviser la description en lignes
      const splitDescription = doc.splitTextToSize(description, maxWidth);
      splitDescription.forEach(line => {
        if (yPos > 250) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, margin, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Informations techniques
    doc.setFontSize(12);
    doc.setTextColor(COLORS.primary);
    doc.text("INFORMATIONS TECHNIQUES", margin, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`Créé le : ${mainCourante.created_at_formatted}`, margin, yPos);
    yPos += 6;
    
    if (mainCourante.updated_at_formatted && mainCourante.updated_at_formatted !== mainCourante.created_at_formatted) {
      doc.text(`Modifié le : ${mainCourante.updated_at_formatted}`, margin, yPos);
      yPos += 6;
    }
    
    // Pied de page
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Généré le ${new Date().toLocaleDateString('fr-FR')} - SecureControle`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    
    // Sauvegarder le PDF
    const fileName = `main_courante_${mainCourante.id}_${mainCourante.created_at_formatted.replace(/[\/: ]/g, '_')}.pdf`;
    doc.save(fileName);
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la génération du PDF:", error);
    return false;
  }
};

const MainCouranteList = () => {
  const navigate = useNavigate();
  const [mainCourantes, setMainCourantes] = useState([]);
  const [agents, setAgents] = useState([]); // Pour le filtre par agent
  const [sites, setSites] = useState([]);   // Pour le filtre par site
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [societeInfo, setSocieteInfo] = useState(null);

  // Logo + coordonnées de la société connectée, embarqués en en-tête des PDF exportés.
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    axios.get(`${API_BASE_URL}/societes/my_logo.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const infoLigne = [res.data?.address_societe, res.data?.tel_societe].filter(Boolean).join(" — ");
        setSocieteInfo({
          logo: res.data?.logo_base64 || null,
          nom: res.data?.nom_societe || null,
          infoLigne: infoLigne || null,
        });
      })
      .catch(() => {});
  }, []);

  // Filtres
  const [search, setSearch] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedMotif, setSelectedMotif] = useState("");
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  // Récupérer les agents et sites pour les filtres
  const fetchAgentsAndSites = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    try {
      // Récupérer les agents
      const agentsResponse = await axios.get(`${API_BASE_URL}/agents/list.php?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (agentsResponse.data?.success) {
        setAgents(agentsResponse.data.agents || []);
      }

      // Récupérer les sites
      const sitesResponse = await axios.get(`${API_BASE_URL}/sites/list.php?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (sitesResponse.data?.success) {
        setSites(sitesResponse.data.sites || sitesResponse.data.data || []);
      }
    } catch (err) {
      console.error("Erreur chargement agents/sites:", err);
    }
  }, []);

  // Récupérer les mains courantes
  const fetchMainCourantes = useCallback(async (page = 1) => {
    setLoading(true);
    setServerError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setServerError("Token d'authentification manquant.");
      setLoading(false);
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedAgent) params.append("id_agent", selectedAgent);
      if (selectedSite) params.append("id_site", selectedSite);
      if (selectedMotif) params.append("motif", selectedMotif);
      params.append("page", page);
      params.append("limit", pagination.limit);
      
      const url = `${API_BASE_URL}/main_courante/list.php?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        setMainCourantes(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        
        if (response.data.data?.length === 0) {
          setInfoMsg("Aucune main courante trouvée.");
        } else {
          setInfoMsg("");
        }
      } else {
        setServerError(response.data?.message || "Erreur de chargement.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Accès interdit. Vous devez être gestionnaire.");
      } else if (!err.response) {
        setServerError("Serveur indisponible.");
      } else {
        setServerError(`Erreur: ${err.response.status}`);
      }
    } finally {
      setLoading(false);
    }
  }, [search, selectedAgent, selectedSite, selectedMotif, pagination.limit, navigate]);

  useEffect(() => {
    fetchAgentsAndSites();
    fetchMainCourantes();
  }, [fetchMainCourantes, fetchAgentsAndSites]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchMainCourantes(1);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedAgent("");
    setSelectedSite("");
    setSelectedMotif("");
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchMainCourantes(1);
  };

  const exportToCSV = () => {
    if (mainCourantes.length === 0) {
      setInfoMsg("Aucune donnée à exporter.");
      return;
    }

    const headers = [
      "ID",
      "Date",
      "Site",
      "Agent",
      "Matricule",
      "Motif",
      "Description"
    ];

    const csvData = mainCourantes.map(mc => [
      mc.id,
      mc.created_at_formatted,
      mc.site_nom,
      mc.agent_nom,
      mc.agent_matricule,
      mc.motif,
      mc.description?.replace(/"/g, '""') || ''
    ]);

    const csvContent = [
      headers.join(";"),
      ...csvData.map(row => row.join(";"))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `mains-courantes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setInfoMsg(`Export CSV réussi (${mainCourantes.length} enregistrements)`);
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Main courante</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Main courante</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-journal-text"></i>
                    </div>
                    Journal des mains courantes
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Consultation en temps réel des observations et incidents reportés par les agents
                </p>
              </div>
              
              {/* Bouton d'export avec menu déroulant */}
              <div className="dropdown">
                <button
                  className="btn fw-semibold dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  style={{
                    color: COLORS.success,
                    borderColor: COLORS.success,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.success;
                    e.target.style.color = COLORS.white;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(76, 175, 80, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.success;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <i className="bi bi-download me-2"></i> 
                  Exporter
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button 
                      className="dropdown-item" 
                      onClick={exportToCSV}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <i className="bi bi-file-earmark-excel me-2" style={{ color: COLORS.success }}></i>
                      Exporter en CSV
                    </button>
                  </li>
                  <li>
                    <button 
                      className="dropdown-item" 
                      onClick={() => {
                        if (mainCourantes.length === 0) {
                          setInfoMsg("Aucune donnée à exporter.");
                          return;
                        }
                        if (generateMainCourantesReportPDF(mainCourantes, societeInfo)) {
                          setInfoMsg(`Rapport PDF généré avec succès (${mainCourantes.length} enregistrements).`);
                        } else {
                          setServerError("Erreur lors de la génération du rapport PDF.");
                        }
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <i className="bi bi-file-pdf me-2" style={{ color: COLORS.danger }}></i>
                      Exporter en PDF
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            {/* Filtres SIMPLIFIÉS */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <form onSubmit={handleSearch} className="row g-3">
                      {/* Ligne 1 */}
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher (site, agent, matricule)..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person"></i>
                          </span>
                          <select
                            className="form-select"
                            value={selectedAgent}
                            onChange={(e) => setSelectedAgent(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les agents</option>
                            {agents.map(agent => (
                              <option key={agent.id_agent} value={agent.id_agent}>
                                {agent.prenom_agent} {agent.nom_agent} ({agent.matricule_agent})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-building"></i>
                          </span>
                          <select
                            className="form-select"
                            value={selectedSite}
                            onChange={(e) => setSelectedSite(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les sites</option>
                            {sites.map(site => (
                              <option key={site.id_site} value={site.id_site}>
                                {site.nom_site}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Ligne 2 SIMPLIFIÉE */}
                      <div className="col-md-6">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-tag"></i>
                          </span>
                          <select
                            className="form-select"
                            value={selectedMotif}
                            onChange={(e) => setSelectedMotif(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les motifs</option>
                            {MOTIF_OPTIONS.map(motif => (
                              <option key={motif.value} value={motif.value}>
                                {motif.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="d-flex gap-2">
                          <button 
                            type="submit" 
                            className="btn fw-semibold"
                            style={{
                              backgroundColor: COLORS.primary,
                              color: COLORS.white,
                              borderRadius: '8px',
                              padding: '10px 20px',
                              flex: 1,
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <i className="bi bi-search me-2"></i> 
                            Appliquer
                          </button>
                          
                          <button
                            type="button"
                            className="btn fw-semibold"
                            onClick={clearFilters}
                            style={{
                              color: COLORS.primary,
                              borderColor: COLORS.primary,
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '10px',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
                <div className="mt-3">
                  <button 
                    className="btn btn-sm fw-semibold"
                    onClick={() => fetchMainCourantes()}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      padding: '6px 16px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = COLORS.white;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i> 
                    Réessayer
                  </button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des mains courantes...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `2px solid ${COLORS.accent}`
                      }}>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '15%' }}>Date/Heure</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '20%' }}>Site</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '20%' }}>Agent</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '15%' }}>Motif</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '20%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mainCourantes.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <div className="mb-4">
                              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                                width: '80px',
                                height: '80px',
                                backgroundColor: '#F5F7FA',
                                color: '#B0BEC5'
                              }}>
                                <i className="bi bi-journal-text fs-1"></i>
                              </div>
                            </div>
                            <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucune main courante trouvée</h6>
                            <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                              {search || selectedAgent || selectedSite || selectedMotif
                                ? "Aucune main courante ne correspond à vos critères de recherche."
                                : "Aucune main courante n'a été enregistrée pour le moment."}
                            </p>
                            {search || selectedAgent || selectedSite || selectedMotif ? (
                              <button
                                className="btn fw-semibold mt-3"
                                onClick={clearFilters}
                                style={{
                                  color: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  backgroundColor: 'transparent',
                                  borderRadius: '8px',
                                  padding: '8px 20px',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#E3F2FD';
                                  e.target.style.borderColor = COLORS.secondary;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = COLORS.primary;
                                }}
                              >
                                <i className="bi bi-x-lg me-2"></i>
                                Effacer les filtres
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ) : (
                        mainCourantes.map((mc) => (
                          <tr key={mc.id} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                            
                            
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <div>
                                <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                                  {mc.created_at_formatted}
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                  {mc.updated_at_formatted && mc.updated_at_formatted !== mc.created_at_formatted ? (
                                    <>Modifié: {mc.updated_at_formatted}</>
                                  ) : (
                                    <>Non modifié</>
                                  )}
                                </small>
                              </div>
                            </td>
                            
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <div>
                                <div style={{ color: COLORS.primary, fontWeight: '500', fontSize: '0.95rem' }}>
                                  {mc.site_nom}
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
              
                                </small>
                              </div>
                            </td>
                            
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <div>
                                <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.95rem' }}>
                                  {mc.agent_nom}
                                </div>
                                <div className="d-flex align-items-center mt-1">
                                  <span className="badge fw-semibold me-2" style={{ 
                                    backgroundColor: COLORS.lightBg,
                                    color: COLORS.darkText,
                                    fontSize: '0.75rem',
                                    padding: '3px 8px'
                                  }}>
                                    <i className="bi bi-person-badge me-1"></i>
                                    {mc.agent_matricule}
                                  </span>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                    
                                  </small>
                                </div>
                              </div>
                            </td>
                            
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <div className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                  width: '24px',
                                  height: '24px',
                                  backgroundColor: getMotifColor(mc.motif).bg,
                                  color: getMotifColor(mc.motif).color
                                }}>
                                  <i className="bi bi-tag"></i>
                                </div>
                                <div>
                                  <div style={{ 
                                    color: COLORS.darkText, 
                                    fontWeight: '500',
                                    fontSize: '0.9rem',
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {mc.motif}
                                  </div>
                                  {mc.description && (
                                    <small className="text-muted" style={{ 
                                      fontSize: '0.8rem',
                                      display: 'block',
                                      maxWidth: '200px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {mc.description}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </td>
                            
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <div className="d-flex gap-2">
                                {/* Bouton Détails avec icône et tooltip */}
                                <Link
                                  to={`/maincourante/details/${mc.id}`}
                                  className="btn fw-semibold position-relative"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Voir les détails"
                                  style={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    backgroundColor: 'transparent',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '0.85rem',
                                    transition: 'all 0.3s ease',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = COLORS.primary;
                                    e.target.style.color = COLORS.white;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = COLORS.primary;
                                  }}
                                >
                                  <i className="bi bi-eye fs-6"></i>
                                </Link>
                                
                                {/* Bouton PDF avec icône et tooltip */}
                                <button
                                  className="btn fw-semibold position-relative"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Télécharger PDF"
                                  onClick={() => {
                                    if (generateMainCourantePDF(mc, societeInfo)) {
                                      setInfoMsg(`PDF de la main courante #${mc.id} généré avec succès.`);
                                    } else {
                                      setServerError("Erreur lors de la génération du PDF.");
                                    }
                                  }}
                                  style={{
                                    color: COLORS.danger,
                                    borderColor: COLORS.danger,
                                    backgroundColor: 'transparent',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '0.85rem',
                                    transition: 'all 0.3s ease',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = COLORS.danger;
                                    e.target.style.color = COLORS.white;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = COLORS.danger;
                                  }}
                                >
                                  <i className="bi bi-file-pdf fs-6"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link fw-semibold" 
                          onClick={() => fetchMainCourantes(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          style={{
                            color: pagination.page <= 1 ? '#B0BEC5' : COLORS.primary,
                            borderColor: '#e0e0e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 4px'
                          }}
                        >
                          <i className="bi bi-chevron-left me-1"></i> 
                          Précédent
                        </button>
                      </li>
                      
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <li key={pageNum} className="page-item">
                            <button 
                              className="page-link fw-semibold" 
                              onClick={() => fetchMainCourantes(pageNum)}
                              style={{
                                backgroundColor: pagination.page === pageNum ? COLORS.primary : 'transparent',
                                borderColor: pagination.page === pageNum ? COLORS.primary : '#e0e0e0',
                                color: pagination.page === pageNum ? COLORS.white : COLORS.primary,
                                padding: '8px 16px',
                                borderRadius: '6px',
                                margin: '0 4px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (pagination.page !== pageNum) {
                                  e.target.style.backgroundColor = '#E3F2FD';
                                  e.target.style.borderColor = COLORS.secondary;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (pagination.page !== pageNum) {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#e0e0e0';
                                }
                              }}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      
                      <li className={`page-item ${pagination.page >= pagination.pages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link fw-semibold" 
                          onClick={() => fetchMainCourantes(pagination.page + 1)}
                          disabled={pagination.page >= pagination.pages}
                          style={{
                            color: pagination.page >= pagination.pages ? '#B0BEC5' : COLORS.primary,
                            borderColor: '#e0e0e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 4px'
                          }}
                        >
                          Suivant 
                          <i className="bi bi-chevron-right ms-1"></i>
                        </button>
                      </li>
                    </ul>
                    <div className="text-center mt-3">
                      <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          Page {pagination.page} sur {pagination.pages}
                        </span>
                        <span className="mx-2">•</span>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          Total: {pagination.total} main(s) courante(s)
                        </span>
                      </small>
                    </div>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Script pour initialiser les tooltips Bootstrap */}
      <script dangerouslySetInnerHTML={{
        __html: `
          // Initialiser les tooltips Bootstrap
          document.addEventListener('DOMContentLoaded', function() {
            var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
              return new bootstrap.Tooltip(tooltipTriggerEl);
            });
          });
        `
      }} />

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        /* Styles pour les tooltips */
        .tooltip {
          font-family: 'Roboto', sans-serif;
        }
        
        .tooltip-inner {
          background-color: ${COLORS.primary};
          color: white;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          max-width: 200px;
        }
        
        .tooltip.bs-tooltip-top .tooltip-arrow::before {
          border-top-color: ${COLORS.primary};
        }
        
        .tooltip.bs-tooltip-bottom .tooltip-arrow::before {
          border-bottom-color: ${COLORS.primary};
        }
        
        .tooltip.bs-tooltip-start .tooltip-arrow::before {
          border-left-color: ${COLORS.primary};
        }
        
        .tooltip.bs-tooltip-end .tooltip-arrow::before {
          border-right-color: ${COLORS.primary};
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .d-flex.gap-2 {
            flex-wrap: wrap;
          }
          
          .pagination {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </Layout>
  );
};

export default MainCouranteList;